import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { AssistantChat } from "../components/AssistantChat";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Sparkles, 
  Instagram, 
  Clock,
  Zap,
  Package,
  Layers,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  MessageSquareText,
  X,
  Users
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const BulkGeneratorPage = () => {
  const { user, refreshUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [platform, setPlatform] = useState("instagram");
  const [templateStyle, setTemplateStyle] = useState("professional");
  const [templates, setTemplates] = useState({});
  const [generating, setGenerating] = useState(false);
  const [generatedResults, setGeneratedResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenBalance, setTokenBalance] = useState(0);
  // Creative brief captured from the inline XEA Assistant chat. When set,
  // it's forwarded to /content/generate/bulk and shapes every caption +
  // creative image so the whole batch matches the requested vibe.
  const [creativeBrief, setCreativeBrief] = useState("");
  // Reference image uploaded inside the chat. Description is appended to
  // the creative_brief and the image URL is forwarded as a style anchor so
  // the AI generates each product in the same visual style as the ref.
  const [referenceRef, setReferenceRef] = useState(null); // {image_url, description}

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, templatesRes, tokensRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/content/templates`),
        axios.get(`${API}/tokens/balance`)
      ]);
      setProducts(productsRes.data);
      setTemplates(templatesRes.data.templates || {});
      setTokenBalance(tokensRes.data.tokens || 0);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleToggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleGenerate = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    setGenerating(true);
    setGeneratedResults(null);

    try {
      // Compose final brief — if a reference image is attached, embed its
      // visual description into the brief so it flows into every prompt.
      let finalBrief = creativeBrief.trim();
      if (referenceRef?.description) {
        const refLine = `\n\nVISUAL REFERENCE (match this aesthetic exactly): ${referenceRef.description}`;
        finalBrief = (finalBrief + refLine).slice(0, 1500);
      }
      const response = await axios.post(`${API}/content/generate/bulk`, {
        product_ids: selectedProducts,
        platform: platform,
        template_style: templateStyle,
        creative_brief: finalBrief || null,
        reference_image_url: referenceRef?.image_url || null,
      });
      
      setGeneratedResults(response.data);
      toast.success(`Generated ${response.data.total_generated} pieces of content!`);
      refreshUser();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to generate content";
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ink-950"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="bulk-generator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight flex items-center gap-3">
          <Layers className="w-8 h-8 text-coral-500" />
          Bulk Content Generator
        </h1>
        <p className="text-ink-500 mt-1">Generate marketing content for multiple products at once</p>
      </div>

      {/* Status Bar */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-ink-200">
          <Clock className="w-4 h-4 text-ink-500" />
          <span className="text-sm text-ink-700">
            Tokens: <span className="text-ink-950 font-semibold">{tokenBalance}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-coral-500/10 border border-coral-500/20">
          <Zap className="w-4 h-4 text-coral-500" />
          <span className="text-sm text-violet-300">
            Selected: {selectedProducts.length} products
          </span>
        </div>
        {creativeBrief.trim() && (
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-ink-950 text-white"
            data-testid="creative-brief-chip"
          >
            <MessageSquareText className="w-4 h-4 text-citrus-500" />
            <span className="text-xs font-bold uppercase tracking-[0.18em]">
              Creative brief locked in
            </span>
            <button
              type="button"
              onClick={() => setCreativeBrief("")}
              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 hover:bg-coral-500 transition-colors"
              data-testid="clear-creative-brief-btn"
              aria-label="Clear brief"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Inline XEA Assistant — chat to shape the creative brief for every
          post in this batch. Free to chat; tokens only charged on Generate. */}
      <div
        className="relative rounded-3xl bg-gradient-to-br from-ink-50 via-white to-coral-500/5 border border-ink-200/70 p-1"
        data-testid="bulk-assistant-wrap"
      >
        <div className="rounded-[1.35rem] bg-white">
          <div className="px-5 pt-4 pb-2 flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-ink-950 shrink-0">
              <Sparkles className="w-4 h-4 text-citrus-500" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral-500">
                XEA Assistant · free to chat
              </p>
              <p className="text-sm font-display font-bold text-ink-950">
                Tell me the vibe — or attach a picture I should copy
              </p>
              <p className="text-xs text-ink-500 mt-0.5">
                Try: &ldquo;Make these moody luxe with gold accents&rdquo; or paste/upload a reference image with
                📎 — I&apos;ll style every product in your batch the same way.
              </p>
            </div>
          </div>
          <div className="px-2 pb-2">
            <AssistantChat
              variant="embed"
              contextHint="Describe the vibe or 📎 attach a picture I should match…"
              usePromptLabel="Use as brief"
              enableImageUpload={true}
              onReferenceAttached={setReferenceRef}
              onUsePrompt={(text) => {
                setCreativeBrief(text.slice(0, 1500));
                toast.success("Creative brief locked in for this batch");
              }}
            />
          </div>
        </div>
      </div>

      {/* Editable creative brief — final text fed into the AI prompts */}
      <div className="p-6 rounded-2xl bg-white border border-ink-200 space-y-3" data-testid="creative-brief-editor">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-ink-950 flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-coral-500" />
            Creative Brief (optional)
          </Label>
          {creativeBrief.trim() && (
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
              {creativeBrief.length}/1500
            </span>
          )}
        </div>
        <Textarea
          value={creativeBrief}
          onChange={(e) => setCreativeBrief(e.target.value.slice(0, 1500))}
          placeholder="Optional — paste from the chat above, or write your own. e.g. 'High-end editorial, warm cream &amp; matte gold palette, confident copy, no emojis.'"
          className="bg-ink-100 border-ink-200 text-ink-950 min-h-[88px]"
          data-testid="creative-brief-textarea"
        />
        <p className="text-xs text-ink-500">
          Applies to every caption and creative image in this batch. Leave empty for the default tone.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Product Selection */}
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-white border border-ink-200 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink-950">Select Products</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                className="border-ink-200 text-ink-700 hover:bg-ink-100"
                data-testid="select-all-btn"
              >
                {selectedProducts.length === products.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {products.length === 0 ? (
              <div className="p-8 rounded-xl bg-ink-100 text-center">
                <Package className="w-12 h-12 text-ink-500 mx-auto mb-3" />
                <p className="text-ink-500">No products yet</p>
                <Button
                  variant="link"
                  className="text-coral-500 mt-2"
                  onClick={() => window.location.href = "/dashboard/designer"}
                >
                  Add your first product
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {products.map((product) => {
                  const isSelected = selectedProducts.includes(product.id);
                  return (
                    <label
                      key={product.id}
                      htmlFor={`bulk-product-${product.id}`}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all focus-within:ring-2 focus-within:ring-coral-500/40 focus-within:ring-offset-1 ${
                        isSelected
                          ? "bg-coral-500/10 border-coral-500/40"
                          : "bg-ink-100 border-ink-200 hover:border-ink-300"
                      }`}
                      data-testid={`product-item-${product.id}`}
                    >
                      <input
                        id={`bulk-product-${product.id}`}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleProduct(product.id)}
                        data-testid={`bulk-product-${product.id}`}
                        aria-label={`Select ${product.name}`}
                        className="w-4 h-4 rounded border-ink-300 text-coral-500 focus:ring-coral-500/40 cursor-pointer accent-coral-500"
                      />
                      {product.image_url && (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-ink-950 text-sm font-medium truncate">{product.name}</p>
                        <p className="text-ink-500 text-xs truncate">{product.category}</p>
                      </div>
                      {product.price && (
                        <span className="text-coral-500 text-sm font-semibold">${product.price}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Generation Settings */}
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-white border border-ink-200 space-y-6">
            <h2 className="text-lg font-semibold text-ink-950">Generation Settings</h2>

            {/* Platform Selection */}
            <div className="space-y-2">
              <Label className="text-ink-950">Target Platform</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPlatform("instagram")}
                  className={`p-4 rounded-xl border transition-all ${
                    platform === "instagram"
                      ? "bg-gradient-to-br from-purple-600 to-pink-600 border-transparent"
                      : "bg-ink-100 border-ink-200 hover:border-ink-300"
                  }`}
                  data-testid="bulk-platform-instagram"
                >
                  <Instagram className={`w-6 h-6 mx-auto mb-2 ${platform === "instagram" ? "text-ink-950" : "text-ink-500"}`} />
                  <span className={`text-sm font-medium ${platform === "instagram" ? "text-ink-950" : "text-ink-700"}`}>
                    Instagram
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setPlatform("tiktok")}
                  className={`p-4 rounded-xl border transition-all ${
                    platform === "tiktok"
                      ? "bg-ink-50 border-transparent ring-2 ring-white"
                      : "bg-ink-100 border-ink-200 hover:border-ink-300"
                  }`}
                  data-testid="bulk-platform-tiktok"
                >
                  <TikTokIcon className={`w-6 h-6 mx-auto mb-2 ${platform === "tiktok" ? "text-ink-950" : "text-ink-500"}`} />
                  <span className={`text-sm font-medium ${platform === "tiktok" ? "text-ink-950" : "text-ink-700"}`}>
                    TikTok
                  </span>
                </button>
              </div>
            </div>

            {/* Template Style Selection */}
            <div className="space-y-2">
              <Label className="text-ink-950">Caption Style</Label>
              <Select value={templateStyle} onValueChange={setTemplateStyle}>
                <SelectTrigger className="bg-ink-100 border-ink-200 text-ink-950 h-12" data-testid="template-select">
                  <SelectValue placeholder="Choose a style..." />
                </SelectTrigger>
                <SelectContent className="bg-ink-100 border-ink-200">
                  {Object.entries(templates).map(([key, template]) => (
                    <SelectItem 
                      key={key} 
                      value={key}
                      className="text-ink-950 hover:bg-ink-200"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{template.name}</span>
                        <span className="text-xs text-ink-500">{template.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || selectedProducts.length === 0}
              className="w-full h-14 bg-coral-500 hover:bg-coral-600 text-lg gap-2"
              data-testid="bulk-generate-btn"
            >
              {generating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generating {selectedProducts.length} items...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate {selectedProducts.length} Contents
                </>
              )}
            </Button>
          </div>

          {/* Results Panel */}
          {generatedResults && (
            <div className="p-6 rounded-2xl bg-white border border-ink-200 space-y-4">
              <div className="flex items-center gap-2 text-coral-500">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Bulk generation complete!</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-ink-100">
                  <p className="text-2xl font-bold text-ink-950">{generatedResults.total_generated}</p>
                  <p className="text-sm text-ink-500">Generated</p>
                </div>
                {generatedResults.skipped_count > 0 && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <p className="text-2xl font-bold text-amber-400">{generatedResults.skipped_count}</p>
                    <p className="text-sm text-ink-500">Skipped</p>
                  </div>
                )}
              </div>

              <Button
                onClick={() => window.location.href = "/dashboard/gallery"}
                className="w-full bg-ink-100 hover:bg-ink-200 gap-2"
                data-testid="go-to-review-btn"
              >
                Review Generated Content <ArrowRight className="w-4 h-4" />
              </Button>

              {/* Share to community CTA */}
              <button
                type="button"
                onClick={() => window.location.href = "/dashboard/community"}
                data-testid="bulk-share-to-community"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-coral-500/30 bg-gradient-to-r from-coral-500/10 to-amber-500/10 text-ink-950 hover:from-coral-500/20 hover:to-amber-500/20 transition-all text-sm font-bold"
              >
                <Users className="w-4 h-4 text-coral-500" />
                Share your favorites to the community →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkGeneratorPage;
